import './StreamingOrderStatus.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Page } from '@Components/Page';
import { FullPageSpinner } from '@Components/Spinner';
import { useSetAreYouSure, useSetError } from '../../redux/modals';
import {
  awaitJSON,
  fetchCometSelfInvoke,
  StreamingOrdersLambdaFetch
} from '../../utils/fetch-utils';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import { useIsMounted } from '../../utils/hooks/useDOMHelpers';
import { useTabbedNav } from '../../utils/hooks/useNav';
import { GenerateView } from './GenerateView/GenerateView';
import { TagData } from './GenerateView/utils';
import { OrderView } from './OrderView/OrderView';
import { OrderPdfEntry, StreamingOrder } from './OrderView/order-view-utils';

const TabKey = {
  ORDERS: 'orders',
  GENERATE: 'generate'
} as const;
type TabKeyType = (typeof TabKey)[keyof typeof TabKey];
const NAVS: TabKeyType[] = [TabKey.ORDERS, TabKey.GENERATE];

export const StreamingOrderStatus = () => {
  const { cid: company } = useCompanyInfo();
  const getIsMounted = useIsMounted();

  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();

  const { tab, goToTab } = useTabbedNav<TabKeyType>({
    baseURL: 'streaming/status',
    defaultKey: TabKey.ORDERS,
    navs: NAVS
  });

  const [orderInfo, setOrderInfo] = useState<StreamingOrder[]>();
  const [pdfInfo, setPdfInfo] = useState<Record<string, OrderPdfEntry>>();
  const [tagInfo, setTagInfo] = useState<TagData[]>();
  const [shouldRefreshData, setShouldRefreshData] = useState(false);

  useEffect(() => {
    if (!orderInfo || shouldRefreshData) {
      (async () => {
        try {
          const res = await StreamingOrdersLambdaFetch('/order_info', {
            params: { company, includePdfs: true }
          });
          const data = await awaitJSON<StreamingOrder[]>(res);
          if (getIsMounted()) {
            setOrderInfo(data);
            setShouldRefreshData(false);
          }
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [orderInfo, company, setError, getIsMounted, shouldRefreshData]);

  useEffect(() => {
    if (!pdfInfo || shouldRefreshData) {
      (async () => {
        try {
          const lambdaResult = await fetchCometSelfInvoke<
            { company: string },
            Record<string, OrderPdfEntry>
          >(StreamingOrdersLambdaFetch, '/pdf_info', { company }, { method: 'GET' });
          if (getIsMounted()) {
            setPdfInfo(lambdaResult);
            setShouldRefreshData(false);
          }
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [pdfInfo, company, setError, getIsMounted, shouldRefreshData]);

  useEffect(() => {
    if (!tagInfo || shouldRefreshData) {
      (async () => {
        try {
          const res = await StreamingOrdersLambdaFetch('/tag_info', { params: { company } });
          const data = await awaitJSON<TagData[]>(res);
          if (getIsMounted()) {
            setTagInfo(data);
            setShouldRefreshData(false);
          }
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [tagInfo, company, setError, getIsMounted, shouldRefreshData]);

  const [saving, setSaving] = useState(false);

  const forceCommit = useCallback(
    async (id: string | number) => {
      try {
        await setAreYouSure({
          title: 'Force-Commit This Order?',
          message: `You are about to force commit order ${id}. Are you sure?`,
          okayText: 'Force Commit'
        });
      } catch {
        return;
      }
      setSaving(true);
      try {
        await StreamingOrdersLambdaFetch('/orders/status', {
          method: 'POST',
          body: { id, status: 20 }
        });
        setOrderInfo(undefined);
        setTagInfo(undefined);
      } catch (e) {
        setError({
          message: `Failed to commit order. Error: ${e.message}.`,
          reportError: e
        });
      } finally {
        setSaving(false);
      }
    },
    [setError, setAreYouSure]
  );

  const [suppressedMap, setSuppressedMap] = useState<Record<string, boolean>>({});

  const generateLabel = useMemo(() => {
    if (!tagInfo) return 'Generate';
    const missingCount = tagInfo.reduce(
      (count, line) =>
        line.hasPlacement || line.hasOverride || suppressedMap[line.derivedID] ? count : count + 1,
      0
    );
    return `Generate Tags (${missingCount})`;
  }, [tagInfo, suppressedMap]);

  return (
    <Page
      title='Streaming Order Status'
      pageType='Streaming Order Status'
      navs={[
        { label: 'Orders', key: TabKey.ORDERS },
        { label: generateLabel, key: TabKey.GENERATE }
      ]}
      selectedNav={tab}
      onNav={key => goToTab(key as TabKeyType)}
    >
      <div className='streaming-order-status'>
        {orderInfo && tagInfo ? (
          <div className='tab-content'>
            {tab === TabKey.ORDERS && (
              <OrderView orderInfo={orderInfo} pdfInfo={pdfInfo} forceCommit={forceCommit} />
            )}
            {tab === TabKey.GENERATE && (
              <GenerateView
                tagInfo={tagInfo}
                suppressedMap={suppressedMap}
                setSuppressedMap={setSuppressedMap}
                shouldRefreshData={shouldRefreshData}
                setShouldRefreshData={setShouldRefreshData}
              />
            )}
          </div>
        ) : (
          <FullPageSpinner />
        )}
        {saving && (
          <div className='saving'>
            <FullPageSpinner />
          </div>
        )}
      </div>
    </Page>
  );
};
